/* eslint-disable no-unused-vars */
import { useNavigate } from 'react-router-dom';
import { planStatusEnum } from 'common/utils/enums';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import { isPropertyBulkBilling } from 'common/utils/misc';
import StatusLabel from 'common/components/status-label';
import { InfoField } from 'common/components/info-card';
import { allPlanStatusOptions } from 'common/utils/selectOptions';
import { useEffect } from 'react';
import moment from 'moment';
import Button from 'common/components/button';
import { FaArrowRight } from 'react-icons/fa6';

// Function to determine if the expiration date should be shown
const showExpirationDate = (plan) => {
  const reviewStatuses = [
    planStatusEnum.REVIEW,
    planStatusEnum.ACTIVATION_REQUIRED,
  ]
  const inReview = reviewStatuses.includes(plan?.status)

  // If the plan is paid or in review, do not show expiration date
  if (inReview) {
    return false;
  }

  return true;
};

const getNavURL = (plan, id) => {
  // Figure out which step to navigate to based on conditions we know about the plan
  let navigationUrl = '';
  const status = plan?.status;

  // Check if the plan is complete, rejected, expired, or cancelled
  // These should not be clickable
  const isComplete =
    status === planStatusEnum.COMPLETE ||
    status === planStatusEnum.REJECTED ||
    status === planStatusEnum.CANCELLED ||
    status === planStatusEnum.EXPIRED || 
    status === planStatusEnum.NOT_ELIGIBLE
  ;

  // Check if the plan is active or in review
  // These should navigate to the plan details page
  const isActive = status === planStatusEnum.ACTIVE;

  // Check if the plan requires activation
  // These should navigate to the activation page
  const isActivationRequired =
    status === planStatusEnum.ACTIVATION_REQUIRED ||
    status === planStatusEnum.REVIEW;

  // Check if the user has already started purchase
  const isPending = status === planStatusEnum.PENDING;

  // Set the navigation URL and inferred step number based on the conditions above
  if (isComplete) {
    navigationUrl = '';
  } else if (isActive) {
    navigationUrl = `/dashboard/plans/${id}`;
  } else if (isActivationRequired) {
    navigationUrl = `/dashboard/plans/activate/${id}`;
  } else if (isPending) {
    navigationUrl = `/dashboard/plans/purchase/${id}`;
  } else {
    navigationUrl = `/dashboard/plans/quote/${id}`;
  }

  return { navigationUrl };
};

const PlansTable = ({ plans, mini = false }) => {
  // DATA INTIAILIZATION
  const navigate = useNavigate();

  const completeStatuses = [
    planStatusEnum.REJECTED,    
    planStatusEnum.CANCELLED,    
    planStatusEnum.SUSPENDED,
    planStatusEnum.EXPIRED,
    planStatusEnum.NOT_ELIGIBLE,
  ] 

  return (
    <table className='flex flex-col w-full'>
      {/* body */}
      <tbody className='w-full rounded-lg border border-gray-200 divide-y'>
        {plans.map((plan, index) => {
          return (
            <>
              <tr
                key={index}
                onClick={() => navigate(getNavURL(plan, plan.id).navigationUrl)}
                className={`grid w-full hover:bg-gray-50 px-3 py-2 cursor-pointer justify-center ${mini ? 'grid-cols-2 xl:grid-cols-[2fr_2fr_1fr]' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-[1fr_0.6fr_0.4fr_0.4fr_120px]'}`}
              >
                {/* status and ID */}
                <td className='w-full p-3 flex flex-col gap-2 justify-center col-span-2 sm:col-span-3 md:col-span-4 xl:col-span-1'>
                  <InfoField title='Plan Number'>
                    <div className='inline-flex gap-3 items-center'>
                      <span className='uppercase font-mono text-sm px-2 py-0.5 border border-gray-200 rounded-md '>
                        {plan.id}
                      </span>
                      <StatusLabel
                        status={
                          allPlanStatusOptions.find(
                            (option) => option.value === plan.status
                          ).label
                        }
                        size='sm'
                        colorScheme={
                          plan.status === planStatusEnum.ACTIVE
                            ? 'green'
                            : plan.status === planStatusEnum.REVIEW ||
                                plan.status ===
                                  planStatusEnum.ACTIVATION_REQUIRED
                              ? 'yellow'
                              : plan.status === planStatusEnum.EXPIRED || plan.status === planStatusEnum.NOT_ELIGIBLE
                                ? 'red'
                                : 'gray'
                        }
                      />
                    </div>
                  </InfoField>
                </td>

                {/* address, start date, end date */}
                <td className={`flex flex-col gap-1 w-full p-3 text-base font-medium items-start ${!mini && 'col-span-2 sm:col-span-1'}`}>
                  {plan?.property?.id ? (
                    <InfoField title='Community'>
                      {plan?.property?.name}
                    </InfoField>
                  ) : plan?.lease?.address?.streetAddress1 ? (
                    <InfoField title='Address'>
                      <span className='text-base'>
                        {plan?.lease?.address?.streetAddress1}
                      </span>
                    </InfoField>
                  ) : (
                    <InfoField title='Address'>
                      <span className='text-base text-gray-500 italic'>
                        No address provided
                      </span>
                    </InfoField>
                  )}
                </td>

                {/* started application */}
                {!mini && (
                  <td className='flex-col gap-1 w-full p-3 font-medium items-start justify-center hidden md:flex'>
                    <InfoField title='Started On'>
                      <span className='text-base'>
                        {formatDateToHumanDate(plan?.createdAt || '')}
                      </span>
                    </InfoField>
                  </td>
                )}
                {!mini && plan.status !== planStatusEnum.NOT_ELIGIBLE ? (
                  <td className='flex flex-col gap-1 w-full p-3 font-medium items-start justify-center'>
                    {showExpirationDate(plan) && plan.expirationDate ? (
                      <InfoField
                        title={
                          plan.status === planStatusEnum.EXPIRED
                            ? 'Expired'
                            : 'Expires'
                        }
                      >
                        <span className='text-base'>
                          {moment.utc(plan.expirationDate).isSame(moment.utc(), 'day')
                            ? 'Today'
                            : formatDateToHumanDate(plan.expirationDate)}
                        </span>
                      </InfoField>
                    ) : (
                      <InfoField title='Expires'>
                        <span className='text-base'>N/A</span>
                      </InfoField>
                    )}
                  </td>
                ) : null}

                {/* button */}
                <td className='w-full p-3 flex flex-col gap-2 justify-center items-end'>
                  {!completeStatuses.includes(plan.status) && (
                    <Button
                      title={
                        plan.status === planStatusEnum.ACTIVATION_REQUIRED
                          ? 'Activate Plan'
                          : plan.status === planStatusEnum.PENDING
                            ? 'Purchase'
                            : plan.status === planStatusEnum.REVIEW
                              ? 'View Plan'
                              : plan.status === planStatusEnum.QUOTE
                                ? 'View Quote'
                                : 'View Plan'
                      }
                      theme={
                        plan.status === planStatusEnum.ACTIVATION_REQUIRED
                          ? 'primary'
                          : plan.status === planStatusEnum.PENDING
                            ? 'primary'
                            : 'secondary'
                      }
                      wFull={false}
                      fixedHeight={false}
                      size='sm'
                      Icon={FaArrowRight}
                    />
                  )}
                </td>
              </tr>
            </>
          );
        })}
      </tbody>
    </table>
  );
};

export default PlansTable;
